import { LoadDropdown, useFormContext } from 'framework/form';
import useContactService from 'domain/sales/contact/shared/contactService';
import type { ListResponse } from 'framework/list';
import { ADD_NEW_CONTACT_OPTION_VALUE } from './constants';
import type { ContactOption, ContactSearchResult, InviteUserFormData } from './types';
import { useState } from 'react';

type ClientPortalInviteUserContactDropdownProps = {
  accountAlias: string;
  selectedContactId?: string;
};

const getContactEmail = (contact: ContactSearchResult) => contact.email || contact.emails?.[0] || '';

const getContactName = (contact: ContactSearchResult) => {
  return `${contact.firstName || ''} ${contact.lastName || ''}`.trim() || getContactEmail(contact) || contact.id;
};

const mapContactToOption = (contact: ContactSearchResult): ContactOption => ({
  value: contact.id,
  label: getContactName(contact),
});

const addNewContactOption: ContactOption = {
  value: ADD_NEW_CONTACT_OPTION_VALUE,
  label: 'Add new contact',
};

const ClientPortalInviteUserContactDropdown = ({
  accountAlias,
  selectedContactId,
}: ClientPortalInviteUserContactDropdownProps) => {
  const { search } = useContactService();
  const { onChangeCallback } = useFormContext();
  const [contactsById, setContactsById] = useState<Record<string, ContactSearchResult>>({});

  const loadContacts = async (): Promise<ListResponse<ContactOption>> => {
    const response = (await search({
      filter: {
        'account.alias': { eq: accountAlias },
      },
    })) as ListResponse<ContactSearchResult>;
    const contacts = response.result || [];

    setContactsById(
      contacts.reduce<Record<string, ContactSearchResult>>((acc, contact) => {
        acc[contact.id] = contact;
        return acc;
      }, {}),
    );

    return {
      ...response,
      result: [addNewContactOption, ...contacts.map(mapContactToOption)],
    };
  };

  const onContactChange = ({ selectedContactId: nextSelectedContactId }: InviteUserFormData) => {
    const nextIsNewContact = nextSelectedContactId === ADD_NEW_CONTACT_OPTION_VALUE;
    const selectedContact = nextSelectedContactId ? contactsById[nextSelectedContactId] : undefined;

    onChangeCallback?.({
      selectedContactId: nextSelectedContactId,
      contactId: nextIsNewContact ? null : nextSelectedContactId || null,
      email: selectedContact ? getContactEmail(selectedContact) : undefined,
      firstName: selectedContact?.firstName,
      lastName: selectedContact?.lastName,
      isNewContact: nextIsNewContact,
    });
  };

  return (
    <LoadDropdown
      source="selectedContactId"
      label="Select user from contact list"
      loadDataMethod={loadContacts}
      value={selectedContactId}
      placeholder="Select contact"
      onChangeCallback={onContactChange}
    />
  );
};

export default ClientPortalInviteUserContactDropdown;
